import { ɵɵCdkScrollable } from '@angular/cdk/drag-drop';
import { ɵɵCdkVirtualForOf, ɵɵCdkVirtualScrollViewport } from '@angular/cdk/overlay';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, HostListener, inject, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatListOption, MatSelectionList } from '@angular/material/list';
import { MatMenu, MatMenuContent, MatMenuTrigger } from '@angular/material/menu';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { LetDirective, PushPipe } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { FullscreenLayoutAreas, FullscreenLayoutTemplate, layoutTemplatesAreas } from '@src/assets/css/fullscreen.template.scss';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { SYSTEM_VS_GUID } from '@src/suite/common/enums/constants';
import { AppName } from '@src/suite/common/enums/enums';
import { NavigationDirection } from '@src/suite/common/enums/navigation-direction';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import {
    FullscreenActions,
    LayoutChangeEvent,
    OpenFullscreenModalActions,
    selectCurrentRecord,
    selectCurrentSearchItemHeights,
    selectCurrentStoryIndex,
    selectCurrentStoryRelatives,
    selectFullscreenCurrentStory,
    selectFullscreenPinnedItems,
    selectFullscreenRecordType,
    selectFullscreenRundownSelectedLayout,
    selectFullscreenSearchItems,
    selectIsCurrentStoryStandalone,
    selectLayoutTemplate,
    selectLayoutTemplateClass,
    selectPinnedItemGuid,
    selectSearchTreeControl,
    selectSearchTreeDataSource,
    selectSidecarVisible,
    SidecarEfffectActions,
    SidecarMenu,
} from '@src/suite/common/store/fullscreen';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectCurrentNodeUuid } from '@src/suite/common/store/list/list.selectors';
import { selectCurrentApplication } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { selectCurrentViewState, selectIsSearchViewStateType } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { selectCurrentNodeIndex, selectTotalGroupedItem, selectTotalGroupedItemCount } from '@src/suite/common/store/playbook/playbook.selector';
import { toLuxonLocaleDateString } from '@src/suite/common/utils/date-utils';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';

import { SimpleSearchBoxComponent } from '../components/search-box/simple-search-box/simple-search-box';
import { StAccordionComponent } from '../components/st-expansion-panel/st-accordion.component';
import { StExpansionPanelComponent } from '../components/st-expansion-panel/st-expansion-panel.component';
import { CardLeftBorderDirective } from '../directives/card-left-border';
import { DifferentSizesVirtualScrollDirective } from '../directives/different-sizes-virtual-scroll';
import { ClickStopPropagationDirective } from '../directives/stop-propagation';
import { ViewStateTypes } from '../enums/viewstate-types';
import { StFullScreenRecordIconComponent } from './record-mat-icon/record-mat-icon';
import { StRundownFullScreenDialogComponent } from './st-rundown-full-screen/st-rundown-full-screen';
import { SidecarComponent } from './st-sidecar-full-screen/st-sidecar-full-screen';
import { StoriesTabsFullScreenDialogComponent } from './stories-full-screen/stories-full-screen';
import { TopicFullScreenDialogComponent } from './topic-full-screen/topic-full-screen';

@Component({
    selector: 'st-full-screen-dialog',
    imports: [
        CardLeftBorderDirective,
        ClickStopPropagationDirective,
        DifferentSizesVirtualScrollDirective,
        FormsModule,
        LetDirective,
        MatButton,
        MatButtonToggle,
        MatButtonToggleGroup,
        MatFormField,
        MatIcon,
        MatIconButton,
        MatInput,
        MatListOption,
        MatMenu,
        MatMenuContent,
        MatMenuTrigger,
        MatPrefix,
        MatSelectionList,
        MatSuffix,
        MatTooltip,
        NgClass,
        ɵɵCdkScrollable,
        ɵɵCdkVirtualForOf,
        ɵɵCdkVirtualScrollViewport,
        PushPipe,
        SidecarComponent,
        SimpleSearchBoxComponent,
        StAccordionComponent,
        StExpansionPanelComponent,
        StFullScreenRecordIconComponent,
        StoriesTabsFullScreenDialogComponent,
        StRundownFullScreenDialogComponent,
        TopicFullScreenDialogComponent,
    ],
    templateUrl: './st-full-screen-dialog.html',
    styleUrl: './st-full-screen-dialog.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class StFullScreenDialogComponent {
    public store = inject(Store);
    dialogData: {
        hideNavigation: boolean;
    } = inject(MAT_DIALOG_DATA);
    ObjectTypes = ObjectTypes;
    PSTFieldNames = PSTFieldNames;
    FullscreenLayoutAreas = FullscreenLayoutAreas;
    NavigationDirection = NavigationDirection;

    readonly virtualScrollViewPort = viewChild(CdkVirtualScrollViewport);
    readonly searchBox = viewChild<ElementRef>('searchBox');
    PSTWebTotalResultsLabel: I18nKeys = I18nKeys.PSTWebTotalResultsLabel;
    templateItems: FullscreenLayoutTemplate[] = Object.keys(layoutTemplatesAreas) as FullscreenLayoutTemplate[];

    record = this.store.selectSignal(selectCurrentRecord);
    story = this.store.selectSignal(selectFullscreenCurrentStory);
    searchItems = this.store.selectSignal(selectFullscreenSearchItems);
    topbarItems = this.store.selectSignal(selectFullscreenPinnedItems);
    languageSettings = this.store.selectSignal(selectI18N);
    selectedPinItemGuid = this.store.selectSignal(selectPinnedItemGuid);
    selectedTemplate = this.store.selectSignal(selectLayoutTemplate);
    currentViewRecordIdx = this.store.selectSignal(selectCurrentNodeIndex);
    totalViewStateRecordsCount = this.store.selectSignal(selectTotalGroupedItemCount);
    totalGroupedItems = this.store.selectSignal(selectTotalGroupedItem);
    isCurrentStoryStandalone = this.store.selectSignal(selectIsCurrentStoryStandalone);
    currentStoryIndex = this.store.selectSignal(selectCurrentStoryIndex);
    fullScreenRecordType = this.store.selectSignal(selectFullscreenRecordType);
    currentStoryRelatives = this.store.selectSignal(selectCurrentStoryRelatives);
    currentNodeUuid = this.store.selectSignal(selectCurrentNodeUuid);
    isSearchViewStateType = this.store.selectSignal(selectIsSearchViewStateType);
    currentViewState = this.store.selectSignal(selectCurrentViewState);
    currentApplication = this.store.selectSignal(selectCurrentApplication);

    sidecartMainVisible$ = this.store.select(selectSidecarVisible(FullscreenLayoutAreas.SIDECAR_MAIN));
    sidecartSecondVisible$ = this.store.select(selectSidecarVisible(FullscreenLayoutAreas.SIDECAR_SECOND));
    sidecartThirdVisible$ = this.store.select(selectSidecarVisible(FullscreenLayoutAreas.SIDECAR_THIRD));
    sidecartFourthVisible$ = this.store.select(selectSidecarVisible(FullscreenLayoutAreas.SIDECAR_FOURTH));
    templateLayoutClass$ = this.store.select(selectLayoutTemplateClass);
    cardItemHeights$ = this.store.select(selectCurrentSearchItemHeights);
    treeDataSource$ = this.store.select(selectSearchTreeDataSource);
    selectTreeControl$ = this.store.select(selectSearchTreeControl);

    readonly pinItemsSearchTerm = signal('');
    readonly hoveredItemGuid = signal('');
    readonly searchInput = signal('');
    readonly pinnedItemsSerachTerm = signal('');

    readonly showStoriesNavigation = computed(() => {
        return this.fullScreenRecordType() === ObjectTypes.SCRIPT && !this.isCurrentStoryStandalone() && !this.isSearchViewStateType();
    });

    readonly currentIdx = computed(() => {
        if (this.showStoriesNavigation()) {
            return this.currentStoryIndex() + 1;
        } else {
            return this.currentViewRecordIdx();
        }
    });

    readonly currentMenuItem = computed(() => {
        const idx = this.currentIdx() - 1;

        if (idx > -1) {
            const items = this.matMenuSearchOptions();
            return items[idx];
        }
    });

    readonly totalCount = computed(() => {
        if (this.showStoriesNavigation()) {
            return this.currentStoryRelatives().length;
        } else {
            return this.totalViewStateRecordsCount();
        }
    });

    readonly recordType = computed(() => {
        if (this.isSearchViewStateType() || this.currentViewState()?.PSTViewStateType === ViewStateTypes.ASSIGNMENT) {
            return this.languageSettings().PSTObjectTypeRecord;
        }
        switch (this.record()?.Type) {
            case ObjectTypes.TOPIC:
                return this.languageSettings().PSTObjectTypeTopic;
            case ObjectTypes.ASSIGNMENT:
                return this.languageSettings().PSTObjectTypeAssignment;
            case ObjectTypes.SCRIPT:
                return this.languageSettings().PSTObjectTypeStory;
            case ObjectTypes.RUNDOWN:
                return this.languageSettings().PSTObjectTypeRundown;
            default:
                break;
        }
        return '';
    });

    readonly matMenuSearchOptions = computed(() => {
        const trimmedSearchInput = this.searchInput().trim();
        const getTooltip = (rec: StorytellingRecord) => {
            if (rec?.ContainerTitle) {
                const containerData = `${rec?.Title} [${rec?.ContainerTitle} ${toLuxonLocaleDateString(rec?.ContainerModTime)}]`;
                return `${rec?.FullStorySlug || containerData} `;
            } else {
                return rec?.Title;
            }
        };
        if (this.showStoriesNavigation()) {
            const currentStoryRelatives = this.currentStoryRelatives().map(story => ({
                uniqueId: story?.Guid,
                object: story,
                containerModTimeDate: toLuxonLocaleDateString(story?.ContainerModTime),
                tooltip: getTooltip(story),
            }));
            if (!this.searchInput()) {
                return currentStoryRelatives;
            } else {
                return currentStoryRelatives.filter(story => story?.object?.Title?.toLowerCase().includes(trimmedSearchInput.toLowerCase()));
            }
        } else {
            if (!this.searchInput()) {
                return this.totalGroupedItems()?.map(node => ({
                    uniqueId: node.uniqueId,
                    object: node.object,
                    containerModTimeDate: toLuxonLocaleDateString(node.object?.ContainerModTime),
                    tooltip: getTooltip(node.object),
                }));
            } else
                return this.totalGroupedItems()
                    ?.map(node => ({
                        uniqueId: node.uniqueId,
                        object: node.object,
                        containerModTimeDate: toLuxonLocaleDateString(node.object?.ModTime),
                        tooltip: getTooltip(node.object),
                    }))
                    ?.filter(record => record?.object?.Title?.toLowerCase().includes(trimmedSearchInput.toLowerCase()));
        }
    });

    readonly selectedTemplateIndex = computed(() => {
        return this.templateItems.indexOf(this.selectedTemplate());
    });

    readonly filteredPinnedItems = computed(() => {
        const searchTerm = this.pinnedItemsSerachTerm();
        return this.topbarItems().filter(item => item?.Title?.toLowerCase().includes(searchTerm.toLowerCase()));
    });

    readonly noBorderIndexes = computed(() => {
        if (!this.selectedPinItemGuid()) {
            return [];
        }
        const selectedItemIdx = this.topbarItems().findIndex(item => item?.Guid === this.selectedPinItemGuid());
        if (selectedItemIdx === -1) {
            return [];
        }
        if (selectedItemIdx === this.topbarItems().length - 1) {
            return [selectedItemIdx];
        }
        return [selectedItemIdx, selectedItemIdx + 1];
    });

    readonly isCreate = computed(() => {
        return this.record()?.isNewRecord;
    });

    readonly hideNavigation = computed(() => {
        return this.dialogData.hideNavigation;
    });

    readonly topicData = computed(() => {
        return this.store.selectSignal<StorytellingTopicRecord>(selectCurrentRecord)();
    });

    readonly roLayout = computed(() => {
        return (
            this.store.selectSignal(selectFullscreenRundownSelectedLayout)() ??
            (this.record() as StorytellingRundownRecord)?.DefaultLayout ??
            (this.record() as StorytellingRundownRecord)?.TemplateLayout
        );
    });

    readonly isNavigationHidden = computed(() => {
        switch (this.currentApplication()) {
            case AppName.LIVE:
                return false;
            case AppName.CAST:
            case AppName.DISCOVER:
                if (this.story()) {
                    return false;
                }
                return true;
            default:
                return false;
        }
    });

    @HostListener('document:keyup.arrowleft', ['$event'])
    public navigateBack(event: Event) {
        const parentIsMe = (event.target as HTMLElement).closest('st-full-screen-dialog');
        if (!parentIsMe) {
            return;
        }
        this.navigate(event, NavigationDirection.PREVIOUS);
    }
    @HostListener('document:keyup.arrowright', ['$event'])
    public navigateForward(event: Event) {
        const parentIsMe = (event.target as HTMLElement).closest('st-full-screen-dialog');
        if (!parentIsMe) {
            return;
        }
        this.navigate(event, NavigationDirection.NEXT);
    }

    onSearchInputChange(event: Event) {
        this.searchInput.set((event.target as HTMLInputElement).value);
    }

    clearSearch(event: Event) {
        event.stopPropagation();
        this.searchInput.set('');
        this.onMenuOpened();
    }

    onMenuOpened() {
        const input = this.searchBox().nativeElement as HTMLInputElement;
        input.focus();
    }

    onPinnedItemChanged(itemGuid: string, type: ObjectTypes) {
        if (itemGuid && type) {
            switch (type) {
                case ObjectTypes.TOPIC:
                    this.store.dispatch(FullscreenActions.openTopic({ recordGuid: itemGuid, recordType: type, fromPinnedItem: true }));
                    break;
                case ObjectTypes.SCRIPT:
                    this.store.dispatch(FullscreenActions.tryOpenStory({ recordGuid: itemGuid, recordType: type, fromPinnedItem: true }));
                    break;
                case ObjectTypes.RUNDOWN:
                    this.store.dispatch(FullscreenActions.openRundown({ recordGuid: itemGuid, recordType: type, fromPinnedItem: true }));
            }
        }
    }

    onTemplateChanged(event: MatButtonToggleChange) {
        this.store.dispatch(FullscreenActions.setFullscreenTemplate({ template: event.value }));
    }

    searchItemExecute(searchTerm: string) {
        this.pinItemsSearchTerm.set(searchTerm);
        this.store.dispatch(FullscreenActions.setSearchTerm({ searchTerm }));
    }

    searchPinnedItemExecute(searchTerm: string) {
        this.pinnedItemsSerachTerm.set(searchTerm);
    }

    closeModal() {
        this.store.dispatch(OpenFullscreenModalActions.closeFullscreenModal());
    }

    selectItem(guid: string, type: ObjectTypes) {
        switch (type) {
            case ObjectTypes.TOPIC:
                this.store.dispatch(FullscreenActions.openTopic({ recordGuid: guid, recordType: type, fromPinnedItem: true }));
                break;
            case ObjectTypes.SCRIPT:
                this.store.dispatch(FullscreenActions.openStory({ recordGuid: guid, recordType: type, fromPinnedItem: true }));
                break;
            case ObjectTypes.RUNDOWN:
                this.store.dispatch(FullscreenActions.openRundown({ recordGuid: guid, recordType: type, fromPinnedItem: true }));
                break;
        }
    }

    hoverItem(guid: string) {
        this.hoveredItemGuid.set(guid);
    }

    pinItem(itemGuid: string) {
        this.store.dispatch(FullscreenActions.pinItem({ itemGuid }));
    }

    unpinItem(itemGuid: string) {
        this.store.dispatch(FullscreenActions.unpinItem({ itemGuid }));
    }

    openHomeItem() {
        this.store.dispatch(FullscreenActions.openHomeItem());
    }

    trackByGuid(_index, node) {
        if (node) {
            return node?.object?.Guid || node?.id || node;
        }
        return node;
    }

    toggleNode(treeControl, node) {
        treeControl.toggle(node);
    }

    onPinItemMenuOpened() {
        this.virtualScrollViewPort().checkViewportSize();
    }

    public navigate(event, direction: NavigationDirection) {
        if (
            event &&
            event.target &&
            event.target.tagName &&
            !(event.target.tagName.toLowerCase() === 'input' || event.target.tagName.toLowerCase() === 'textarea' || event.target.classList.contains('fr-element'))
        ) {
            this.store.dispatch(FullscreenActions.navigateRecord({ direction, throughTopicStories: this.showStoriesNavigation() }));
        }
    }

    /**
     * Navigates to a different record directly.
     * If the current navigation is on search results,it will navigate to the next search record.
     * Else If the current navigation is on stories,
     * then it will navigate to the next story. Otherwise, it will navigate to the next record
     * of the given type.
     * @param nextRecordGuid The GUID of the record to navigate to
     */
    navigateDirectly(nextRecordGuid: string) {
        this.searchInput.set('');
        this.store.dispatch(
            FullscreenActions.navigateRecordDirectly({
                uniqueNodeId: nextRecordGuid,
                throughTopicStories: this.showStoriesNavigation(),
            })
        );
    }

    openRoStory(guid: string) {
        this.store.dispatch(
            SidecarEfffectActions.openStoryInSidecar({
                guid,
                sidecarType: FullscreenLayoutAreas.SIDECAR_MAIN,
                sidecarMenu: SidecarMenu.STORIES,
            })
        );
    }

    changeRoLayout(event: LayoutChangeEvent) {
        this.store.dispatch(FullscreenActions.setRundownLayout({ layout: event, roGuid: this.record().Guid }));
    }

    onAssignmentOpenHandler(record: StorytellingAssignmentRecord) {
        this.store.dispatch(FullscreenActions.openAssignment({ recordGuid: record.Guid, recordType: ObjectTypes.ASSIGNMENT, sidecarMenu: FullscreenLayoutAreas.SIDECAR_MAIN, vsGuid: SYSTEM_VS_GUID }));
    }

    onDiscoverItemOpenHandler(record: StorytellingRecord) {
        this.store.dispatch(FullscreenActions.openDiscoverItem({ recordGuid: record.Guid, recordType: ObjectTypes.DISCOVER_ITEM }));
    }

    onStoryOpenHandler(story: StorytellingStoryRecord) {
        this.store.dispatch(FullscreenActions.tryOpenStory({ recordGuid: story.Guid, recordType: story.Type }));
    }
}
